import { useState, useEffect, useMemo } from 'react';
import { Shield, Search, ShieldAlert, ChevronRight, X, Copy, Terminal, FileClock, Download, FileSpreadsheet, FileJson, User } from 'lucide-react';
import { useAdminStore, AuditLogItem } from '../../../store/adminStore';
import { Pagination } from '../../../components/Pagination';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../../components/DropdownMenu';
import { AdminPageHeader, AdminEmptyState, AdminSkeletonTable } from '../components';
import apiClient from '../../../lib/api';
import toast from 'react-hot-toast';

export function AuditLog() {
  const { auditLogs, auditTotal, stats, isLoading, loadAuditLogs, loadStats } = useAdminStore();
  
  const [activeTab, setActiveTab] = useState<'all' | 'auth' | 'rbac' | 'attendance' | 'support' | 'lockouts'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState<'All' | 'Success' | 'Failure'>('All');
  const [actionFilter, setActionFilter] = useState('All');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [selectedLog, setSelectedLog] = useState<AuditLogItem | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(new Date());

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  useEffect(() => {
    loadStats();
    loadAuditLogs();
  }, [loadStats, loadAuditLogs]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        loadAuditLogs(),
        loadStats(),
      ]);
      setLastRefreshed(new Date());
      toast.success('Audit trail refreshed');
    } catch {
      toast.error('Failed to refresh audit log');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      const response = await apiClient.get('/admin/audit-logs/export', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const pad = (n: number) => String(n).padStart(2, '0');
      const now = new Date();
      const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Audit logs CSV exported');
    } catch {
      toast.error('Failed to export CSV');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportJson = () => {
    try {
      const jsonStr = JSON.stringify(displayedLogs, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const pad = (n: number) => String(n).padStart(2, '0');
      const now = new Date();
      const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('Audit logs JSON exported');
    } catch {
      toast.error('Failed to export JSON');
    }
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success(`Copied ${field} to clipboard`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Helper: map each audit log to its canonical domain category
  const getEventCategory = (log: AuditLogItem): 'auth' | 'rbac' | 'attendance' | 'support' | 'platform' => {
    const act = (log.action || '').toLowerCase();
    const res = (log.resource_type || '').toLowerCase();

    // 1. RBAC & Roles
    if (
      act.includes('role') ||
      act.includes('perm') ||
      act.includes('matrix') ||
      res.includes('role') ||
      res.includes('perm')
    ) {
      return 'rbac';
    }

    // 2. Auth & Identity
    if (
      act.startsWith('auth') ||
      act.includes('login') ||
      act.includes('logout') ||
      act.includes('impersonat') ||
      act.includes('session') ||
      act.includes('mfa') ||
      act.includes('invite') ||
      act.includes('password') ||
      res === 'user'
    ) {
      return 'auth';
    }

    // 3. Operations & Attendance / HR
    if (
      act.includes('attendance') ||
      act.includes('check_in') ||
      act.includes('check_out') ||
      act.includes('regularization') ||
      act.includes('location') ||
      res.includes('attendance') ||
      res.includes('office')
    ) {
      return 'attendance';
    }

    // 4. Support & Tickets
    if (
      act.includes('ticket') ||
      act.includes('leave') ||
      act.includes('support') ||
      res.includes('ticket') ||
      res.includes('leave')
    ) {
      return 'support';
    }

    return 'platform';
  };

  // Helper: check if event is a warning, failure or anomaly
  const isEventFailedOrWarning = (log: AuditLogItem): boolean => {
    const act = (log.action || '').toLowerCase();
    const metaStr = JSON.stringify(log.metadata || {}).toLowerCase();
    return (
      act.includes('fail') ||
      act.includes('denied') ||
      act.includes('error') ||
      act.includes('warn') ||
      act.includes('reject') ||
      act.includes('lock') ||
      act.includes('suspend') ||
      act.includes('missing_checkout') ||
      metaStr.includes('exception') ||
      metaStr.includes('error') ||
      metaStr.includes('failed')
    );
  };

  // Human readable label formatter
  const formatActionLabel = (action: string) => {
    const act = (action || '').toLowerCase().trim();
    if (act === 'role_created' || act === 'role.create') return 'Role Created';
    if (act === 'role_updated' || act === 'role.update') return 'Role Updated';
    if (act === 'role_deleted' || act === 'role.delete') return 'Role Deleted';
    if (act === 'assign_role' || act === 'role.assign' || act === 'user.role') return 'Role Assigned';
    if (act === 'remove_role') return 'Role Removed';
    if (act === 'user_roles_modified') return 'User Roles Modified';
    if (act === 'auth_impersonate_user') return 'User Impersonation Started';
    if (act === 'auth_stop_impersonation') return 'User Impersonation Ended';
    if (act === 'login_success' || act === 'user_login_success' || act === 'login') return 'User Login Succeeded';
    if (act.includes('login_fail') || act.includes('auth_fail')) return 'User Login Failed';
    if (act === 'user_invited' || act.includes('invite')) return 'Account Invitation Dispatched';
    if (act.includes('mfa')) return 'MFA Policy Altered';
    if (act === 'check_in_created') return 'Check-in Recorded';
    if (act === 'check_out_created') return 'Check-out Recorded';
    if (act === 'attendance_policy_updated') return 'Attendance Policy Updated';
    if (act === 'leave_ticket_created') return 'Leave Request Ticket Created';
    if (act === 'leave_ticket_backfilled') return 'Leave Ticket Backfilled';
    if (act === 'leave_approved_via_support') return 'Leave Approved via Support';
    if (act === 'support_ticket_cancelled_via_leave') return 'Support Ticket Cancelled via Leave';
    if (act === 'support_ticket_rejected_via_leave') return 'Support Ticket Rejected via Leave';
    if (act === 'session_missing_checkout') return 'Missing Checkout Flagged';
    if (act === 'attendance_auto_logout_hours_removed') return 'Auto Logout Policy Updated';
    if (act === 'location_changed') return 'Office Location Changed';
    if (act === 'adjustment_created') return 'Attendance Adjustment Created';
    if (act === 'request_regularization') return 'Attendance Regularization Requested';
    if (act === 'project.create' || act === 'project_created') return 'Project Created';
    if (act === 'create') return 'Record Created';
    if (act === 'update') return 'Record Updated';
    if (act === 'delete') return 'Record Deleted';

    return action
      .toLowerCase()
      .replace(/[._-]/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatResourceType = (type?: string) => {
    if (!type) return 'Platform';
    const t = type.toLowerCase().trim();
    if (t === 'user') return 'User';
    if (t === 'user_role' || t === 'userrole') return 'User Role';
    if (t === 'role') return 'Role';
    if (t === 'attendance_session') return 'Attendance Session';
    if (t === 'leave_request') return 'Leave Request';
    if (t === 'whiteboard') return 'Whiteboard';
    if (t === 'attendance_policy') return 'Attendance Policy';
    if (t === 'office') return 'Office';
    if (t === 'project') return 'Project';
    if (t === 'attendance_regularization') return 'Attendance Regularization';
    return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getCategoryBadge = (category: string) => {
    switch (category) {
      case 'rbac':
        return { label: 'RBAC & Roles', style: 'bg-primary/10 text-primary border border-primary/20' };
      case 'auth':
        return { label: 'Auth & Login', style: 'bg-secondary text-foreground border border-border' };
      case 'attendance':
        return { label: 'Attendance & Ops', style: 'bg-info/10 text-info border border-info/20' };
      case 'support':
        return { label: 'Support & Tickets', style: 'bg-warning/10 text-warning border border-warning/20' };
      default:
        return { label: 'Platform & Shared', style: 'bg-secondary text-muted-foreground border border-border' };
    }
  };

  // Dynamic Category Counts from live loaded logs
  const categoryCounts = useMemo(() => {
    let auth = 0;
    let rbac = 0;
    let attendance = 0;
    let support = 0;
    let failed = 0;

    auditLogs.forEach((l) => {
      const cat = getEventCategory(l);
      if (cat === 'auth') auth++;
      else if (cat === 'rbac') rbac++;
      else if (cat === 'attendance') attendance++;
      else if (cat === 'support') support++;

      if (isEventFailedOrWarning(l)) failed++;
    });

    return {
      all: auditLogs.length,
      auth,
      rbac,
      attendance,
      support,
      failed,
    };
  }, [auditLogs]);

  // Distinct resource categories and actions for select filters
  const categories = useMemo(() => {
    const set = new Set<string>();
    auditLogs.forEach((l) => {
      if (l.resource_type) set.add(l.resource_type);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [auditLogs]);

  const actions = useMemo(() => {
    const set = new Set<string>();
    auditLogs.forEach((l) => {
      if (l.action) set.add(l.action);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [auditLogs]);

  // Filtered logs
  const displayedLogs = useMemo(() => {
    let list = [...auditLogs];

    // Quick Tab filter
    if (activeTab === 'auth') {
      list = list.filter((l) => getEventCategory(l) === 'auth');
    } else if (activeTab === 'rbac') {
      list = list.filter((l) => getEventCategory(l) === 'rbac');
    } else if (activeTab === 'attendance') {
      list = list.filter((l) => getEventCategory(l) === 'attendance');
    } else if (activeTab === 'support') {
      list = list.filter((l) => getEventCategory(l) === 'support');
    } else if (activeTab === 'lockouts') {
      list = list.filter((l) => isEventFailedOrWarning(l));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (l) =>
          (l.action || '').toLowerCase().includes(q) ||
          formatActionLabel(l.action).toLowerCase().includes(q) ||
          (l.user_email || '').toLowerCase().includes(q) ||
          (l.first_name || '').toLowerCase().includes(q) ||
          (l.last_name || '').toLowerCase().includes(q) ||
          (l.ip_address || '').toLowerCase().includes(q) ||
          (l.resource_type || '').toLowerCase().includes(q)
      );
    }

    if (categoryFilter !== 'All') {
      const cf = categoryFilter.toLowerCase();
      list = list.filter(
        (l) =>
          (l.resource_type || '').toLowerCase() === cf ||
          getEventCategory(l) === cf
      );
    }

    if (actionFilter !== 'All') {
      list = list.filter((l) => (l.action || '').toLowerCase() === actionFilter.toLowerCase());
    }

    if (statusFilter !== 'All') {
      list = list.filter((l) => {
        const isFail = isEventFailedOrWarning(l);
        return statusFilter === 'Success' ? !isFail : isFail;
      });
    }

    list.sort((a, b) => {
      const ta = new Date(a.created_at).getTime();
      const tb = new Date(b.created_at).getTime();
      return sortOrder === 'desc' ? tb - ta : ta - tb;
    });

    return list;
  }, [auditLogs, activeTab, searchQuery, categoryFilter, actionFilter, statusFilter, sortOrder]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(displayedLogs.length / pageSize));
  const validPage = Math.min(Math.max(1, page), totalPages);
  const startIndex = (validPage - 1) * pageSize;
  const paginatedLogs = useMemo(() => {
    return displayedLogs.slice(startIndex, startIndex + pageSize);
  }, [displayedLogs, startIndex, pageSize]);

  return (
    <div className="min-h-full bg-background text-foreground p-4 space-y-4 pb-12">
      {/* Page Header */}
      <AdminPageHeader
        icon={FileClock}
        title="Audit & Security Log"
        subtitle="Immutable platform audit trail of user access, role assignments, authentication and security posture"
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        lastRefreshed={lastRefreshed}
        secondaryAction={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="sm" disabled={isExporting} className="gap-1.5">
                <Download className="size-3.5 text-primary" />
                <span>Export</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-36">
              <DropdownMenuItem onClick={handleExportCsv} className="text-xs cursor-pointer">
                <FileSpreadsheet className="size-3.5 mr-2 text-primary" />
                <span>Export CSV</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportJson} className="text-xs cursor-pointer">
                <FileJson className="size-3.5 mr-2 text-primary" />
                <span>Export JSON</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      <div className="space-y-4">
        {/* Sticky Filters & Category Chips */}
        <div className="bg-card border border-border rounded-lg p-4 shadow-xs space-y-3">
          {/* Category Chips Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-border/80">
            <div className="flex items-center gap-1.5 flex-wrap">
              {[
                { key: 'all', label: 'All Events', count: categoryCounts.all },
                { key: 'auth', label: 'Auth & Login', count: categoryCounts.auth },
                { key: 'rbac', label: 'RBAC & Roles', count: categoryCounts.rbac },
                { key: 'attendance', label: 'Attendance & Ops', count: categoryCounts.attendance },
                { key: 'support', label: 'Support & Tickets', count: categoryCounts.support },
                { key: 'lockouts', label: 'Failed / Warnings', count: categoryCounts.failed },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key as any);
                    setPage(1);
                  }}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer ${
                    activeTab === tab.key
                      ? 'bg-primary text-primary-foreground font-semibold shadow-2xs'
                      : 'bg-secondary/60 text-muted-foreground hover:text-foreground border border-border'
                  }`}
                >
                  <span>{tab.label}</span>
                  <span className="ml-1.5 text-[10px] opacity-80">({tab.count})</span>
                </button>
              ))}
            </div>

            <div className="text-xs text-muted-foreground">
              Showing <strong className="text-foreground">{displayedLogs.length}</strong> matching events
            </div>
          </div>

          {/* Search, Status, Category and Action Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-0.5">
            {/* Search */}
            <div className="relative">
              <Search className="size-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search actor, action, IP..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary shadow-2xs"
              />
            </div>

            {/* Category Dropdown */}
            <div>
              <select
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-1.5 text-xs rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer shadow-2xs"
              >
                <option value="All">All Resource Categories</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{formatResourceType(c)} ({c})</option>
                ))}
              </select>
            </div>

            {/* Action Dropdown */}
            <div>
              <select
                value={actionFilter}
                onChange={(e) => {
                  setActionFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-1.5 text-xs rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer shadow-2xs"
              >
                <option value="All">All Action Types</option>
                {actions.map((a) => (
                  <option key={a} value={a}>{formatActionLabel(a)} ({a})</option>
                ))}
              </select>
            </div>

            {/* Status Dropdown */}
            <div>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as any);
                  setPage(1);
                }}
                className="w-full px-3 py-1.5 text-xs rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer shadow-2xs"
              >
                <option value="All">All Event Statuses</option>
                <option value="Success">Success Only</option>
                <option value="Failure">Failed / Warnings Only</option>
              </select>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden shadow-xs">
          {isLoading && auditLogs.length === 0 ? (
            <AdminSkeletonTable rows={8} cols={5} />
          ) : paginatedLogs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-secondary/40 border-b border-border text-muted-foreground font-semibold uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="py-3 px-4">Event Action</th>
                    <th className="py-3 px-4">Resource Category</th>
                    <th className="py-3 px-4">Actor</th>
                    <th className="py-3 px-4">Timestamp &amp; IP</th>
                    <th className="py-3 px-4 text-center">Status</th>
                    <th className="py-3 px-4 text-right">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {paginatedLogs.map((log) => {
                    const isFailure = isEventFailedOrWarning(log);
                    const catBadge = getCategoryBadge(getEventCategory(log));
                    const actorName = log.first_name || log.last_name
                      ? `${log.first_name || ''} ${log.last_name || ''}`.trim()
                      : log.user_email || 'System';

                    return (
                      <tr
                        key={log.id}
                        onClick={() => setSelectedLog(log)}
                        className="hover:bg-secondary/20 transition-colors cursor-pointer group"
                      >
                        {/* Event Action */}
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2.5">
                            <div className={`size-7 rounded-md flex items-center justify-center shrink-0 ${
                              isFailure ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'
                            }`}>
                              {isFailure ? <ShieldAlert className="size-3.5" /> : <Shield className="size-3.5" />}
                            </div>
                            <div>
                              <span className="font-semibold text-foreground block group-hover:text-primary transition-colors">
                                {formatActionLabel(log.action)}
                              </span>
                              <span className="text-[10px] text-muted-foreground font-mono">
                                {log.action}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Resource Category */}
                        <td className="py-3 px-4">
                          <div className="flex flex-col gap-1 items-start">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border uppercase tracking-wider ${catBadge.style}`}>
                              {catBadge.label}
                            </span>
                            <span className="text-[10px] text-muted-foreground font-mono">
                              {formatResourceType(log.resource_type)}
                            </span>
                          </div>
                        </td>

                        {/* Actor */}
                        <td className="py-3 px-4">
                          <div className="space-y-0.5">
                            <span className="font-medium text-foreground block">{actorName}</span>
                            <span className="text-[11px] text-muted-foreground font-mono">{log.user_email || 'system'}</span>
                          </div>
                        </td>

                        {/* Timestamp & IP */}
                        <td className="py-3 px-4">
                          <div className="space-y-0.5">
                            <span className="text-foreground block">
                              {log.created_at ? new Date(log.created_at).toLocaleString() : 'Recent'}
                            </span>
                            <span className="text-[11px] text-muted-foreground font-mono">
                              {log.ip_address || '127.0.0.1'}
                            </span>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="py-3 px-4 text-center">
                          {isFailure ? (
                            <Badge variant="outline" className="text-[10px] bg-destructive/10 text-destructive border-destructive/30">
                              Failed / Alert
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px] bg-success/10 text-success border-success/30">
                              Success
                            </Badge>
                          )}
                        </td>

                        {/* Inspector Icon */}
                        <td className="py-3 px-4 text-right">
                          <ChevronRight className="size-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all inline" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <AdminEmptyState
              title="No audit events found"
              description="No audit logs matched your active filter criteria."
              icon={FileClock}
              action={{
                label: 'Clear Filters',
                onClick: () => {
                  setSearchQuery('');
                  setCategoryFilter('All');
                  setActionFilter('All');
                  setStatusFilter('All');
                  setActiveTab('all');
                },
                variant: 'secondary',
              }}
            />
          )}

          {/* Pagination */}
          {displayedLogs.length > pageSize && (
            <div className="p-3 border-t border-border flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                Page {validPage} of {totalPages} ({displayedLogs.length} events)
              </span>
              <Pagination
                page={validPage}
                pageCount={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      </div>

      {/* Row Inspector Drawer with Before/After Diff */}
      {selectedLog && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-2xs animate-in fade-in"
          onClick={() => setSelectedLog(null)}
        >
          <div
            className="w-full max-w-xl bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 text-xs"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drawer Header */}
            <div className="p-4 border-b border-border flex items-center justify-between bg-card shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="size-9 rounded-lg bg-primary/10 border border-primary/20 text-primary flex items-center justify-center shrink-0">
                  <Terminal className="size-4" />
                </div>
                <div>
                  <h2 className="font-display text-sm font-semibold text-foreground">
                    {formatActionLabel(selectedLog.action)}
                  </h2>
                  <span className="font-mono text-[10px] text-muted-foreground block">
                    ID: {selectedLog.id}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(JSON.stringify(selectedLog, null, 2), 'Audit Event')}
                  className="h-8 px-2 text-xs"
                  title="Copy JSON"
                >
                  <Copy className="size-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedLog(null)}
                  className="h-8 w-8 p-0"
                >
                  <X className="size-4" />
                </Button>
              </div>
            </div>

            {/* Scrollable Body */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Event Metadata */}
              <div className="rounded-lg border border-border bg-secondary/20 p-3.5 space-y-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Shield className="size-3.5 text-primary" />
                  <span>Event Attributes</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-[11px] text-muted-foreground block">Raw Action</span>
                    <span className="font-mono text-foreground font-semibold">{selectedLog.action}</span>
                  </div>
                  <div>
                    <span className="text-[11px] text-muted-foreground block">Resource Category</span>
                    <span className="font-semibold text-foreground">{selectedLog.resource_type || 'auth'}</span>
                  </div>
                  <div>
                    <span className="text-[11px] text-muted-foreground block">Timestamp</span>
                    <span className="font-mono text-foreground">{selectedLog.created_at ? new Date(selectedLog.created_at).toLocaleString() : '—'}</span>
                  </div>
                  <div>
                    <span className="text-[11px] text-muted-foreground block">IP Address</span>
                    <span className="font-mono text-foreground">{selectedLog.ip_address || '127.0.0.1'}</span>
                  </div>
                </div>
              </div>

              {/* Actor Card */}
              <div className="rounded-lg border border-border bg-secondary/20 p-3.5 space-y-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <User className="size-3.5 text-primary" />
                  <span>Actor Context</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-[11px] text-muted-foreground block">User Name</span>
                    <span className="font-semibold text-foreground">
                      {selectedLog.first_name || selectedLog.last_name
                        ? `${selectedLog.first_name || ''} ${selectedLog.last_name || ''}`.trim()
                        : 'System Operator'}
                    </span>
                  </div>
                  <div>
                    <span className="text-[11px] text-muted-foreground block">Actor Email</span>
                    <span className="font-mono text-foreground">{selectedLog.user_email || 'system'}</span>
                  </div>
                </div>
              </div>

              {/* Before / After State Diff */}
              {(selectedLog.changes || selectedLog.metadata?.changes) && (
                <div className="rounded-lg border border-border bg-card p-3.5 space-y-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground block">
                    Before / After State Diff
                  </span>
                  <div className="grid grid-cols-2 gap-2 text-[11px]">
                    <div className="p-2.5 rounded bg-destructive/10 border border-destructive/20 text-destructive">
                      <p className="font-semibold uppercase text-[9px] mb-1">Previous State (Before)</p>
                      <pre className="font-mono text-[10px] overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify(selectedLog.changes?.before || selectedLog.metadata?.changes?.before || {}, null, 2)}
                      </pre>
                    </div>
                    <div className="p-2.5 rounded bg-success/10 border border-success/20 text-success">
                      <p className="font-semibold uppercase text-[9px] mb-1">New State (After)</p>
                      <pre className="font-mono text-[10px] overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify(selectedLog.changes?.after || selectedLog.metadata?.changes?.after || {}, null, 2)}
                      </pre>
                    </div>
                  </div>
                </div>
              )}

              {/* Raw JSON Payload */}
              <div className="rounded-lg border border-border bg-card p-3.5 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Payload Metadata
                  </span>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(JSON.stringify(selectedLog.metadata || {}, null, 2), 'Metadata')}
                    className="text-[11px] text-primary hover:underline cursor-pointer"
                  >
                    Copy Metadata
                  </button>
                </div>
                <pre className="p-2.5 rounded bg-secondary/50 font-mono text-[10px] text-foreground overflow-x-auto max-h-48">
                  {JSON.stringify(selectedLog.metadata || {}, null, 2)}
                </pre>
              </div>
            </div>

            {/* Sticky Drawer Footer */}
            <div className="p-4 border-t border-border bg-card flex justify-end shrink-0">
              <Button variant="secondary" size="sm" onClick={() => setSelectedLog(null)}>
                Close Inspector
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
